import Vue from 'vue';
import { get } from './axiosHelper';
import challengerEnv from '../lib/challengerEnv.js';
import {
  IPolicyData,
  IPoliciesData,
  IPoliciesResponseItem,
  IPoliciesResponseMaturityDetails,
  isPoliciesResponseMaturityDetails,
  isPoliciesResponseItem,
  isPoliciesData,
  ITransactionsData,
} from './IPoliciesServiceModels';
import {
  IMaturityBanner,
  IPolicyDetails,
  IPoliciesMaturityDetails,
  IPolicies,
  IPolicy,
} from './IPoliciesViewModels';

const policiesPath = 'external/policy-management/v1/api'

const maturityTypeName = {
  DueNoActionRequired: 'DueNoActionRequired',
  DueInstructionsReceived: 'DueInstructionsReceived',
  DueNoInstructionsReceived30_14days: 'DueNoInstructionsReceived30_14days',
  DueNoInstructionsReceived14_0days: 'DueNoInstructionsReceived14_0days',
  DatePassedNoInstructionsReceived: 'DatePassedNoInstructionsReceived',
};

const maturityTypeSortOrder = {
  DueNoActionRequired: 5,
  DueInstructionsReceived: 4,
  DueNoInstructionsReceived30_14days: 3,
  DueNoInstructionsReceived14_0days: 2,
  DatePassedNoInstructionsReceived: 1,
};

/*
Transform one Policy Item from the API service response to the Policy Model for the View
Return Properly formatted View Model (IPoliciesMaturityDetails) - if everything is ok on the inputs
Return Null for any error: json object input is null - or not in correct format
*/
const getMaturityDetails = (data: IPoliciesResponseMaturityDetails) => {
  if (data == null) {
    return null;
  }

  // Validate Type matches IPoliciesResponseMaturityDetails
  if (!isPoliciesResponseMaturityDetails(data)) {
    if(Vue.prototype.$logger && typeof(Vue.prototype.$logger)!=='undefined'){
      Vue.prototype.$logger.debug('getMaturityDetails - isPoliciesResponseMaturityDetails failed: ', data);
    }
    return null;
  }

  try {
    const maturityDetails: IPoliciesMaturityDetails = {
      isInstructionReceived: data.isInstructionReceived,
      maturityDate: data.maturityDate,
      maturityValue: data.maturityValue,
      maturityType: data.maturityType,
    };

    return maturityDetails;
  } catch (error) {
    if(Vue.prototype.$logger && typeof(Vue.prototype.$logger)!=='undefined'){
      Vue.prototype.$logger.warning('getMaturityDetails - catching exception: ', error);
    }
    return null;
  }
};

/*
Transform one Policy Item from the API service response to the Policy Model for the View
Return Properly formatted View Model (IPolicyDetails) - if everything is ok on the inputs
Return Null for any error: json object input is null - or not in correct format
*/
const getPolicy = (data: IPoliciesResponseItem) => {
  if (data == null) {
    return null;
  }

  // Validate Type IPoliciesResponseItem
  if (!isPoliciesResponseItem(data)) {
    if(Vue.prototype.$logger && typeof(Vue.prototype.$logger)!=='undefined'){
      Vue.prototype.$logger.debug('getPolicy - isPoliciesResponseItem failed: ', data);
    }
    return null;
  }

  try {
    const policyDetails: IPolicyDetails = {
      accountNo: data.accountNo,
      accountProductType: data.accountProductType,
      closedDate: data.closedDate,
      investmentOptionId: data.investmentOptionId,
      productName: data.productName,
      balanceAmount: data.balanceAmount,
      nextPaymentAmount: data.nextPaymentAmount,
      nextPaymentDate: data.nextPaymentDate ? new Date(data.nextPaymentDate) : null,
      maturityDetails: getMaturityDetails(data.maturityDetails),
      unitPrice: data.unitPrice,
      units: data.units,
      isActive: data.isActive,
      isRBACashLinked: data.isRBACashLinked,
      centrelinkFlag: data.centrelinkFlag,
      term: data.term,
    };
    return policyDetails;
  } catch (error) {
    if(Vue.prototype.$logger && typeof(Vue.prototype.$logger)!=='undefined'){
      Vue.prototype.$logger.warning('getPolicy - catching exception: ', error);
    }
    return null;
  }
};

/*
Transform one Maturity Banner data from the API service response to the Maturity Banner Model for the View
Return Properly formatted View Model (IMaturityBanner) - if everything is ok on the inputs
Return Null for any error: json object input is null - or not in correct format
*/
const getMaturityBanner = (data: IPoliciesResponseItem) => {
  if (data == null) {
    return null;
  }

  // Validate Type IPoliciesResponseItem
  if (!isPoliciesResponseItem(data)) {
    if(Vue.prototype.$logger && typeof(Vue.prototype.$logger)!=='undefined'){
      Vue.prototype.$logger.debug('getMaturityBanner - isPoliciesResponseItem failed: ', data);
    }
    return null;
  }

  try {
    if (data.maturityDetails == null) {
      return null;
    }

    // Validate Type IPoliciesResponseMaturityDetails
    if (!isPoliciesResponseMaturityDetails(data.maturityDetails)) {
      if(Vue.prototype.$logger && typeof(Vue.prototype.$logger)!=='undefined'){
        Vue.prototype.$logger.debug('getMaturityBanner ', data.maturityDetails);
      }
      return null;
    }

    // CR: it is possible the Maturity Details exists but the Type will be null - this is not a valid banner
    if (data.maturityDetails.maturityType === null) {
      if(Vue.prototype.$logger && typeof(Vue.prototype.$logger)!=='undefined'){
        Vue.prototype.$logger.debug('getMaturityBanner  - maturityDetails.maturityType is null: ', data.maturityDetails);
      }
      return null;
    }

    const maturityBanner: IMaturityBanner = {
      accountNumber: data.accountNo,
      instructionReceived: data.maturityDetails.isInstructionReceived,
      maturityDate: data.maturityDetails.maturityDate
        ? new Date(data.maturityDetails.maturityDate)
        : null,
      nextPaymentDate: data.nextPaymentDate ? new Date(data.nextPaymentDate) : null,
      maturityType: data.maturityDetails.maturityType,
      maturityValue: data.maturityDetails.maturityValue,
      productName: data.productName,
    };

    return maturityBanner;
  } catch (error) {
    if(Vue.prototype.$logger && typeof(Vue.prototype.$logger)!=='undefined'){
      Vue.prototype.$logger.warning('getMaturityBanner - catching exception: ', error);
    }
    return null;
  }
};

/*
Comparer for Maturity Banners - can be used on the Array SORT
We sort includes:
      - first by Type
      - then if same type then by dates
*/
const compareMaturityBanners = (a: IMaturityBanner, b: IMaturityBanner) => {
  // Validate full obejct exists...
  if (a === null && b === null) {
    return 0;
  }
  if (a === null && b !== null) {
    return -1;
  }
  if (a !== null && b === null) {
    return 1;
  }

  if (a.maturityType === b.maturityType) {
    // Sort depending no the date time
    if (a.maturityDate == null && b.maturityDate == null) {
      return 0;
    } else if (a.maturityDate == null && b.maturityDate != null) {
      return -1;
    } else if (a.maturityDate != null && b.maturityDate == null) {
      return 1;
    } else if (a.maturityDate > b.maturityDate) {
      return 1;
    } else if (a.maturityDate < b.maturityDate) {
      return -1;
    } else {
      return 0;
    }
  } else {
    // sort by type
    if (maturityTypeSortOrder[a.maturityType] === maturityTypeSortOrder[b.maturityType]) {
      return 0;
    } else if (maturityTypeSortOrder[a.maturityType] > maturityTypeSortOrder[b.maturityType]) {
      return 1;
    } else {
      return -1;
    }
  }
};

/*
Transform the API response into View Models that can be used on the Vue Side
*/
const transformResponse = (res: IPoliciesData): IPolicies => {
  // Use User defined Type Guard to ensure the structure of the data matches the Interface
  if (!isPoliciesData(res)) {
    if(Vue.prototype.$logger && typeof(Vue.prototype.$logger)!=='undefined'){
      Vue.prototype.$logger.warning('transformResponse - isPoliciesData failed: ', res);
    }
    return null;
  }

  let policiyDetails = new Array<IPolicyDetails>();
  let maturityBanners = new Array<IMaturityBanner>();
  if (res.success) {
    // Loop through ALl policies and build the model
    res.data.forEach((element) => {
      // Building the List of Policies for the overview
      const policy = getPolicy(element as IPoliciesResponseItem);
      if (policy !== null) {
        policiyDetails.push(policy);
      }

      // Building the Maturity Banner list
      const banner = getMaturityBanner(element as IPoliciesResponseItem);
      if (banner !== null) {
        maturityBanners.push(banner);
      }
    });

    const policies: IPolicies = {
      policyDetails: policiyDetails,
      maturityBanners: maturityBanners.sort(compareMaturityBanners),
    };

    return policies;
  } else {
    //TODO: Error handler
  }
  return null;
};

/*
Getting the Maturity banner for a Policy Data object
*/
const getBannerFromPolicy = (res: IPolicy): IMaturityBanner => {
  // Use User defined Type Guard to ensure the structure of the data matches the Interface
  if (res == null) {
    return null;
  }
  try {
    if (res.maturityDetails == null) {
      return null;
    }

    // Validate Type IPoliciesResponseMaturityDetails
    if (!isPoliciesResponseMaturityDetails(res.maturityDetails)) {
      if(Vue.prototype.$logger && typeof(Vue.prototype.$logger)!=='undefined'){
        Vue.prototype.$logger.warning('getBannerFromPolicy - isPoliciesResponseMaturityDetails failed: ', res.maturityDetails);
      }
      return null;
    }

    // CR: it is possible the Maturity Details exists but the Type will be null - this is not a valid banner
    if (res.maturityDetails.maturityType === null) {
      if(Vue.prototype.$logger && typeof(Vue.prototype.$logger)!=='undefined'){
        Vue.prototype.$logger.warning('getBannerFromPolicy - maturityDetails.maturityType null:', res.maturityDetails);
      }
      return null;
    }

    const maturityBanner: IMaturityBanner = {
      accountNumber: res.accountNo,
      instructionReceived: res.maturityDetails.isInstructionReceived,
      maturityDate: res.maturityDetails.maturityDate
        ? new Date(res.maturityDetails.maturityDate)
        : null,
      nextPaymentDate: res.nextPaymentDate ? new Date(res.nextPaymentDate) : null,
      maturityType: res.maturityDetails.maturityType,
      maturityValue: res.maturityDetails.maturityValue,
      productName: res.productName,
    };

    return maturityBanner;
  } catch (error) {
    if(Vue.prototype.$logger && typeof(Vue.prototype.$logger)!=='undefined'){
      Vue.prototype.$logger.error('getBannerFromPolicy - catching exception: ', error);
    }
  return null;
  }
};

/*
Get the Data from API call: Policies data + Maturity Banner
Return the View Models for policies and Maturity
*/
const getPolicies = async (): Promise<IPolicies> => {
  if (challengerEnv().mockApiResponses) {
    const policies = require('../../cypress/fixtures/policies/mock-policies.json'); // simulate API with Mock data...
    const policiesModel = transformResponse(policies);
    return policiesModel;
  }
  else{
    const { customerId } = Vue.prototype.$auth.userDetails;
  const url = `${challengerEnv().apiBaseUrl}/${policiesPath}/investor/policies?showAll=true&showClosed=true`;
    const policies: IPoliciesData = await get(url); // This needs to be enable for the API interaction
    const policiesModel = transformResponse(policies);
    return policiesModel;
    }
};

const getInvestment = async (
  accountNumber: string,
  investmentOptionId?: string
): Promise<IPolicyData> => {
  let url = `${challengerEnv().apiBaseUrl}/${policiesPath}/policies/${accountNumber}`;
  if (investmentOptionId) {
    url = `${url}?investmentoptionid=${investmentOptionId}`;
  }

  if (challengerEnv().mockApiResponses) {
    const policy: IPolicyData = require('../../cypress/fixtures/policies/mockPolicy.json'); // simulate API with Mock data...
    return policy;
  }
  else{
    const policy: IPolicyData = await get(url); // This needs to be enable for the API interaction
    return policy;
  }
};

const getTransactions = async (
  accountNumber: string,
  investmentOptionId?: string,
  pageNumber?: number,
  pageSize?: string,
  sortOrder?: string
): Promise<ITransactionsData> => {
  const url = new URL(`${challengerEnv().apiBaseUrl}/${policiesPath}/policy/${accountNumber}/policytransactions`);
  if (investmentOptionId) {
    url.searchParams.append('investmentoptionid', investmentOptionId);
  }
  if (pageNumber) {
    url.searchParams.append('pageNumber', pageNumber.toString());
  }
  if (pageSize) {
    url.searchParams.append('pageSize', pageSize);
  }
  if (sortOrder) {
    url.searchParams.append('sortOrder', sortOrder);
  }

  if (challengerEnv().mockApiResponses) {
    const policy: ITransactionsData = require('../../cypress/fixtures/transactions/mock-transactions.json'); // simulate API with Mock data...
    return policy;
    }
  else{
    const policy: ITransactionsData = await get(url.href); // This needs to be enable for the API interaction
    return policy;
    }
};

export {
  getInvestment,
  getPolicies,
  getTransactions,
  transformResponse,
  compareMaturityBanners,
  getBannerFromPolicy,
  maturityTypeName,
  maturityTypeSortOrder,
};
